/**
 * The deliberately ANONYMOUS read of this deployment's public identity (#2296).
 *
 * ── WHY NOT THE CONSOLE SDK ────────────────────────────────────────────────
 * MEASURED, and it is the whole reason this file exists: `client.raw()` throws
 * `AuthError: not signed in` before it issues a request. The Console's
 * transport resolves a bearer token per call — correctly, because every other
 * read it makes is a signed-in one — so the one client the app already has is
 * the one client that cannot be used before sign-in. The first wiring of this
 * feature used it, and the symptom was the defect itself: a sign-in screen with
 * no brand, no network request, and nothing in the console.
 *
 * So this is a narrowly scoped transport with exactly one job. It sends the
 * `apikey` the deployment publishes and NO `Authorization` header — not an
 * empty one, not an expired one, none — because the whole point of the H4 door
 * is that it answers an anonymous caller.
 *
 * ── WHY THE OUTCOME IS DISCRIMINATED ───────────────────────────────────────
 * `client.tsx` already carries the warning this file was written against:
 *
 *   "a queryFn written `try { … } catch { return null }` … resolves after
 *    swallowing a 503, so the cache reports a success that never happened"
 *
 * A branding read must never block the gate — somebody signing in to FIX a
 * broken brand must not be stopped by the broken brand — so every failure has
 * to end at the generic card. But "this deployment declared no identity" and
 * "the read never worked" must not be the SAME VALUE, or the fallback hides
 * the wiring fault, which is exactly how the first implementation looked
 * healthy while doing nothing.
 *
 * Both render the card. Only one of them is `unavailable`, and a test can tell
 * them apart.
 */

/** What the read actually did. `none` and `unavailable` render identically and
 *  mean opposite things. */
export type BrandOutcome =
  | { state: 'ok'; doc: Record<string, unknown> }
  | { state: 'none' }            // the deployment declared no identity
  | { state: 'unavailable' }     // the read did not complete — a FAULT

/** Join the deployment origin and REST path without creating a scheme-relative
 *  URL. The shipped web build deliberately uses `/` as its Supabase origin;
 *  concatenating that with `/rest/v1` produces `//rest/v1`, which browsers
 *  interpret as the host `https://rest` rather than this deployment. */
export function publicBrandEndpoint(url: string | undefined, restPath = '/rest/v1'): string | null {
  if (!url) return null
  const origin = url === '/' ? '' : url.replace(/\/+$/, '')
  const path = restPath === '' ? '' : `/${restPath.replace(/^\/+|\/+$/g, '')}`
  return `${origin}${path}/rpc/read_public_projection`
}

/** The URL the projection is read from, assembled the way the console's own
 *  transport assembles one: an empty REST path targets a bare PostgREST at the
 *  origin, while an unset path uses Supabase's `/rest/v1` route. */
function endpoint(): string | null {
  return publicBrandEndpoint(
    import.meta.env.VITE_SUPABASE_URL as string | undefined,
    (import.meta.env.VITE_SUPABASE_REST_PATH as string | undefined) ?? '/rest/v1',
  )
}

export async function readPublicBrand(host: string): Promise<BrandOutcome> {
  const target = endpoint()
  if (!target) return { state: 'unavailable' }
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

  let response: Response
  try {
    response = await fetch(target, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        // PostgREST chooses the RPC namespace from Content-Profile for POST.
        // Without it the live gateway searches its default `access` schema and
        // answers 404 even though the governed door exists in `core`. Keep the
        // response profile explicit too; this transport deliberately bypasses
        // the authenticated SDK which normally supplies both headers.
        'Content-Profile': 'core',
        'Accept-Profile': 'core',
        // The published anon key identifies the DEPLOYMENT, not a person. No
        // `Authorization` header is set at any point in this function, and the
        // browser proof asserts its absence on the wire rather than trusting
        // this comment.
        ...(anonKey ? { apikey: anonKey } : {}),
      },
      body: JSON.stringify({ p_key: 'core.public_brand', p_input: { host } }),
    })
  } catch {
    return { state: 'unavailable' }      // the request never completed
  }

  if (!response.ok) return { state: 'unavailable' }

  let doc: unknown
  try {
    doc = await response.json()
  } catch {
    return { state: 'unavailable' }      // answered, but not with a document
  }

  // `{}` is the projection's honest answer for an unconfigured deployment AND
  // for an unknown host — deliberately identical, so the door is not a host
  // oracle. Neither is a fault.
  if (!doc || typeof doc !== 'object' || Object.keys(doc as object).length === 0) {
    return { state: 'none' }
  }
  return { state: 'ok', doc: doc as Record<string, unknown> }
}
